import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import type { ExternalIdentifier, Rightsholder } from 'types/writer'

import { useGetRightsHolderByIdQuery } from '@disco/state-management/api/rights-holders'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'
import { getLabels } from '@disco/state-management/selectors/contacts'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useUpdateRightsEntity } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'
import { findDuplicateName } from '../../utils/validation'

/*
This component is a little confusing because we want to show the user a view that they are
editing a 'rightsholder' but behind the scenes we are actually operating on 'label' entities.
A label can take on the role of either a 'rightsholder' or 'rightscontroller'
Which are referred to within the app as rightsholder or label
so that's why sometimes we refer to labels and sometimes to rightsholder.
*/
export function EditRightsholderModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { rightsholderId } = useParams<{ rightsholderId: string }>()
  const { splitId, splitsKey } =
    (location.state as RightsDataModalLocationState) || {}

  const rightsholders = useSelector(getLabels)

  const { data: rightsHolderData } = useGetRightsHolderByIdQuery(
    rightsholderId ? parseInt(rightsholderId, 10) : 0,
    { skip: !rightsholderId },
  )

  // Use new API data or fall back to old Redux data
  const rightsholder: Rightsholder | undefined = useMemo(() => {
    return rightsHolderData
      ? {
          id: rightsHolderData.id,
          name: rightsHolderData.title,
          external_identifiers: rightsHolderData.external_identifiers,
        }
      : undefined
  }, [rightsHolderData])

  const [inputValue, setInputValue] = useState(rightsholder?.name || '')

  // Sync inputValue when pro changes (e.g., async load)
  useEffect(() => {
    if (rightsholder?.name) {
      setInputValue(rightsholder.name)
    }
  }, [rightsholder?.name])

  const duplicateRightsholderName = useMemo(
    () =>
      rightsholder
        ? findDuplicateName(inputValue, rightsholders, rightsholder.id)
        : null,
    [inputValue, rightsholders, rightsholder],
  )

  const warningMessage = duplicateRightsholderName
    ? `${duplicateRightsholderName} already exists. Please double-check if this is a duplicate before saving.`
    : null

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const updateEntity = useUpdateRightsEntity({
    entityType: 'Label',
  })

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      if (!rightsholder) return

      await updateEntity(
        rightsholder.id,
        name,
        externalIdentifiers,
        rightsholder.external_identifiers,
      )

      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { rightsholderId: rightsholder.id },
          }),
        )
      }
    },
    [rightsholder, updateEntity, dispatch, splitId, splitsKey],
  )

  if (!rightsholder) {
    return null
  }

  return (
    <BaseDialog
      heading="Edit Rightsholder"
      nameFieldLabel="Rightsholder name"
      initialName={rightsholder.name}
      initialIdentifiers={rightsholder.external_identifiers}
      helperText="Editing this rightsholder will update it everywhere it's used."
      onSave={handleSave}
      isEditMode={true}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showISNI
      showIPI
      open
    />
  )
}

export default EditRightsholderModal
